import { format } from 'date-fns';
import React from 'react';
import { Line, RegularPolygon, Text } from 'react-konva';
import { TwTheme } from '../../../Tw';
import { getBestContrastColor } from '../../common/color.helper';
import { formatTime } from '../../common/formatHelper';

const colors = TwTheme.extend.colors;

export type DeadlineVisualizationProps = {
  deadline: Date;
  startTime?: Date;
  endTime?: Date;
  color: string;
  isSelected: boolean;
  isHovered: boolean;
  stageHeight: number;
  ticksInPixel: number;
  showConnectionLine?: boolean;
  connectionY?: number;
  showTimeGuides?: boolean;
  showFlag?: boolean;
  showLabel?: boolean;
  labelFormat?: 'time' | 'datetime';
  waveHeight?: number; // For wave-specific positioning
  onDeadlineClick?: () => void;
  onDeadlineMouseEnter?: () => void;
  onDeadlineMouseLeave?: () => void;
};

const DeadlineVisualization: React.FC<DeadlineVisualizationProps> = ({
  deadline,
  startTime,
  endTime,
  color,
  isSelected,
  isHovered,
  stageHeight,
  ticksInPixel,
  showConnectionLine = false,
  connectionY,
  showTimeGuides = false,
  showFlag = true,
  showLabel = true,
  labelFormat = 'time',
  waveHeight: eventHeight,
  onDeadlineClick,
  onDeadlineMouseEnter,
  onDeadlineMouseLeave,
}) => {
  const deadlineX = deadline.getTime() / ticksInPixel;
  const startTimeX = startTime ? startTime.getTime() / ticksInPixel : null;
  const endTimeX = endTime ? endTime.getTime() / ticksInPixel : null;

  // Use waveHeight for wave-specific positioning, otherwise use stageHeight
  const heightForPositioning = eventHeight || stageHeight;

  // Calculate opacity based on selection and hover state
  const getOpacity = (baseOpacity: number = 1) => {
    if (isSelected) return baseOpacity;
    if (isHovered) return baseOpacity * 0.5;
    return 0;
  };

  // Event handlers for deadline interaction
  const eventHandlers = {
    onClick: onDeadlineClick,
    onTap: onDeadlineClick,
    onMouseEnter: onDeadlineMouseEnter,
    onMouseLeave: onDeadlineMouseLeave,
  };

  const isWave = !!eventHeight;
  const lineY1 = isWave ? 0 : 0;
  const lineY2 = isWave ? heightForPositioning * 1.25 : stageHeight;

  return (
    <>
      {/* Main deadline vertical line - positioned like in WaveFeature */}
      <Line
        points={[deadlineX, lineY1, deadlineX, lineY2]}
        stroke={color}
        strokeWidth={4}
        opacity={getOpacity(1)}
        lineCap="round"
        strokeScaleEnabled={true}
        perfectDrawEnabled={true}
        {...eventHandlers}
      />

      {/* Deadline flag - positioned like in WaveFeature */}
      {showFlag && (
        <RegularPolygon
          scaleY={-1}
          x={deadlineX - 9}
          y={eventHeight ? eventHeight * 1.28 : stageHeight * 0.5}
          rotation={-30}
          sides={3}
          radius={24}
          fill={color}
          opacity={getOpacity(1)}
          {...eventHandlers}
        />
      )}

      {/* Deadline label - positioned like in WaveFeature */}
      {showLabel && (
        <>
          {/* Optional: background */}
          {/* <Rect
            x={deadlineX - 40}
            y={isWave ? eventHeight * 1.25 : 10}
            width={80}
            height={20}
            fill={color}
            opacity={0.9}
          /> */}
          <Text
            x={deadlineX + 5}
            y={isWave ? eventHeight * 1.5 : 12}
            fill={getBestContrastColor(color)}
            text={
              labelFormat === 'datetime'
                ? format(deadline, 'MMM dd HH:mm')
                : formatTime(deadline)
            }
            fontSize={eventHeight ? eventHeight * 0.1 : 10}
            fontFamily="NeoSans-Regular"
            scaleY={-1}
            opacity={getOpacity(1)}
            {...eventHandlers}
          />
        </>
      )}

      {/* Connection line from job to deadline */}
      {showConnectionLine &&
        startTimeX &&
        connectionY !== undefined &&
        startTimeX !== deadlineX && (
          <Line
            points={[startTimeX, connectionY, deadlineX, connectionY]}
            stroke={color}
            strokeWidth={2}
            dash={[3, 3]}
            opacity={getOpacity(0.8)}
            lineCap="round"
            strokeScaleEnabled={true}
            perfectDrawEnabled={true}
          />
        )}

      {/* Time guides (start and end ticks) */}
      {showTimeGuides && (startTimeX || endTimeX) && (
        <>
          {/* Start time guide */}
          {startTimeX && (
            <>
              <Line
                points={[startTimeX, -170, startTimeX, heightForPositioning]}
                strokeWidth={4}
                stroke={color}
                opacity={getOpacity(0.5)}
                lineCap="round"
                strokeScaleEnabled={true}
                perfectDrawEnabled={true}
                dash={[4, 16]}
              />
              <RegularPolygon
                scaleY={-1}
                x={startTimeX}
                y={-175}
                rotation={30}
                sides={3}
                radius={18}
                fill={color}
                opacity={getOpacity(0.5)}
              />
              <Text
                x={startTimeX - 30}
                y={-185}
                fill={color}
                text={formatTime(startTime)}
                fontSize={heightForPositioning * 0.075}
                fontFamily="NeoSans-Regular"
                scaleY={-1}
                opacity={getOpacity(0.25)}
                align="center"
              />
            </>
          )}

          {/* End time guide */}
          {endTimeX && (
            <>
              <Line
                points={[endTimeX, -170, endTimeX, heightForPositioning]}
                strokeWidth={4}
                stroke={color}
                opacity={getOpacity(0.5)}
                lineCap="round"
                strokeScaleEnabled={true}
                perfectDrawEnabled={true}
                dash={[4, 16]}
              />
              <RegularPolygon
                scaleY={-1}
                x={endTimeX}
                y={-175}
                rotation={-30}
                sides={3}
                radius={18}
                fill={color}
                opacity={getOpacity(0.5)}
              />
              <Text
                x={endTimeX - 30}
                y={-185}
                fill={color}
                text={formatTime(endTime)}
                fontSize={heightForPositioning * 0.075}
                fontFamily="NeoSans-Regular"
                scaleY={-1}
                opacity={getOpacity(0.25)}
                align="center"
              />
            </>
          )}
        </>
      )}
    </>
  );
};

export default DeadlineVisualization;
